/**
 * SampleReport — a static, illustrative preview of the FieldGuide Local
 * Authority Report. NOT a real property, NOT a DB read. Its only job is to
 * show a prospect what they'll see after they run the grader.
 *
 * All data is hand-crafted fiction (invented property, invented queries,
 * invented recommendations). Reuses the real production components so the
 * layout stays 1:1 with the actual report.
 */
import { Link } from "react-router-dom";
import ScoreCard from "@/components/ScoreCard";
import SubScoresPanel from "@/components/SubScoresPanel";
import RestOfPlan from "@/components/RestOfPlan";
import { stripSurroundingQuotes } from "@/lib/text";
import {
  ArrowRight,
  Lock,
  Trophy,
  AlertTriangle,
  PenTool,
  Sparkles,
  Eye,
} from "lucide-react";

const SAMPLE = {
  property: {
    name: "The Cedar House Inn",
    city: "Hudson",
    state: "NY",
    country: "United States",
    website: "https://cedarhousehudson.example",
  },
  completed_at: new Date().toISOString(),
  knownfor_score: 34,
  score_band: "Emerging Local Authority",
  score_band_tone: "Opportunity-focused",
  opportunity_level: "High",
  peer_benchmark_available: true,
  peer_median: 47,
  peer_count: 18,
  peer_delta: -13,
  dramatic_stat:
    "In 18 of 24 travel-planning queries, AI recommended a competitor or an outside guide instead of you.",
  sub_scores: {
    discovery_presence: 51,
    owned_authority: 22,
    competitive_position: 38,
  },
  highlight_won_query: {
    query_text: "best small inns near the Hudson River with a good bar",
    platform_label: "Perplexity",
    per_query_score: 78,
    reasoning:
      "Perplexity cited your official 'Bar & Terrace' page directly and pulled a quote about the seasonal cocktail list. Owned content did the work here.",
  },
  highlight_lost_query: {
    query_text: "what to do in Hudson NY on a rainy Sunday",
    platform_label: "ChatGPT",
    reasoning:
      "The answer named three restaurants, an antique shop, and Basilica Hudson — but never mentioned your property or your published guides. A short 'rainy day itinerary' post from you would almost certainly close this gap.",
  },
  recommendations: [
    {
      rank: 1,
      title: "A rainy-Sunday itinerary through Hudson",
      priority: "High",
      content_type: "Local guide",
      effort: "Low",
      impact: "High",
      why_it_matters:
        "You lost this exact query on ChatGPT and Google. A 600-word guide with 5–7 specific stops, indoor-friendly, closes the gap and pulls in three neighboring queries.",
      linked_query_texts: [
        "what to do in Hudson NY on a rainy Sunday",
        "indoor things to do Hudson Valley winter",
        "romantic weekend Hudson NY",
      ],
    },
    {
      rank: 2,
      title: "Where locals actually eat on Warren Street",
      priority: "High",
      content_type: "Neighborhood guide",
      effort: "Medium",
      impact: "High",
      why_it_matters:
        "Every 'best food in Hudson' query is being answered by Eater and outside blogs. Your team's picks — short notes, honest opinions — are exactly what AI wants to cite.",
    },
    {
      rank: 3,
      title: "The three walks around Cedar House (with photos)",
      priority: "Moderate",
      content_type: "Trail guide",
      effort: "Medium",
      impact: "Moderate",
      why_it_matters:
        "Small hotels rarely publish real trail write-ups — this is uncontested territory.",
    },
    {
      rank: 4,
      title: "A guide to the antique shops (ranked by hours + inventory)",
      priority: "Moderate",
      content_type: "Shopping guide",
      effort: "Low",
      impact: "Moderate",
      why_it_matters:
        "Hudson's antique scene is a top three query cluster and no property in town has a definitive guide.",
    },
    {
      rank: 5,
      title: "Where to take out-of-town friends for their first weekend",
      priority: "Low",
      content_type: "Itinerary",
      effort: "Medium",
      impact: "Moderate",
      why_it_matters:
        "Long-tail 'first time in Hudson' queries are steady traffic year-round.",
    },
  ],
};

export default function SampleReport() {
  const t = SAMPLE;

  // Adapt to the shape ScoreCard and SubScoresPanel expect.
  const tier1 = {
    property: t.property,
    completed_at: t.completed_at,
    knownfor_score: t.knownfor_score,
    score_band: t.score_band,
    score_band_tone: t.score_band_tone,
    opportunity_level: t.opportunity_level,
    peer_benchmark_available: t.peer_benchmark_available,
    peer_median: t.peer_median,
    peer_count: t.peer_count,
    peer_delta: t.peer_delta,
    dramatic_stat: t.dramatic_stat,
    unlocked: true,
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 md:py-12" data-testid="sample-report">
      {/* Sample banner */}
      <div
        className="rounded-2xl mb-8 px-5 py-4 flex items-center gap-4 flex-wrap"
        style={{
          background: "linear-gradient(135deg, rgba(166,124,63,0.10), rgba(107,122,85,0.08))",
          border: "1px solid rgba(166,124,63,0.28)",
        }}
        data-testid="sample-report-banner"
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(166,124,63,0.18)" }}
        >
          <Eye className="w-4 h-4" style={{ color: "var(--brand-gold-deep, #8a6a2f)" }} />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="label-eyebrow mb-0.5">Sample report</div>
          <div className="text-sm leading-relaxed" style={{ color: "var(--text-primary)" }}>
            This is an illustrative preview using an invented property. Your own report will follow
            the same structure with your real hotel&apos;s data.
          </div>
        </div>
        <Link to="/" className="btn-gold text-sm !py-2 !px-4" data-testid="sample-report-cta-top">
          Grade my hotel
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Header */}
      <div className="mb-8 fade-in">
        <div className="flex items-center gap-3 label-eyebrow mb-3">
          <span>FieldGuide Local Authority Report</span>
          <span className="text-[color:var(--text-muted)]">·</span>
          <span className="text-[color:var(--text-muted)]">Sample</span>
        </div>
        <h1 className="font-serif text-3xl md:text-5xl leading-tight">{t.property.name}</h1>
        <div className="text-[color:var(--text-secondary)] mt-2">
          {[t.property.city, t.property.state, t.property.country].filter(Boolean).join(", ")}
          {" · "}
          <span className="italic">invented for illustration</span>
        </div>
      </div>

      {/* Score card — no evidence prop so no modal on the sample */}
      <ScoreCard tier1={tier1} evidence={null} />

      {/* Won / Lost / Dramatic stat */}
      <div className="grid lg:grid-cols-3 gap-4 md:gap-6 mt-6">
        <div className="glass rounded-2xl p-6 relative overflow-hidden">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-[color:var(--score-high)]" />
            <div className="label-eyebrow">Where you win</div>
          </div>
          <div className="font-mono text-sm mb-2 text-[color:var(--text-primary)]">
            &ldquo;{stripSurroundingQuotes(t.highlight_won_query.query_text)}&rdquo;
          </div>
          <div className="text-xs text-[color:var(--text-muted)] mb-3">
            Platform: {t.highlight_won_query.platform_label} · Score{" "}
            {Math.round(t.highlight_won_query.per_query_score)}/100
          </div>
          <div className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
            {t.highlight_won_query.reasoning}
          </div>
        </div>

        <div className="glass rounded-2xl p-6 relative overflow-hidden">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[color:var(--score-low)]" />
            <div className="label-eyebrow">Conversations you&apos;re not in</div>
          </div>
          <div className="font-mono text-sm mb-2 text-[color:var(--text-primary)]">
            &ldquo;{stripSurroundingQuotes(t.highlight_lost_query.query_text)}&rdquo;
          </div>
          <div className="text-xs text-[color:var(--text-muted)] mb-3">
            Platform: {t.highlight_lost_query.platform_label}
          </div>
          <div className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
            {t.highlight_lost_query.reasoning}
          </div>
        </div>

        <div
          className="glass rounded-2xl p-6 lg:col-span-1 relative overflow-hidden"
          style={{
            borderColor: "rgba(107,122,85,0.35)",
            background: "linear-gradient(135deg, rgba(107,122,85,0.08), rgba(166,124,63,0.04))",
          }}
        >
          <div className="label-eyebrow mb-3">The uncomfortable truth</div>
          <div
            className="font-serif text-2xl md:text-3xl leading-tight"
            style={{ color: "var(--text-primary)" }}
          >
            {t.dramatic_stat}
          </div>
        </div>
      </div>

      {/* Unlocked-report body */}
      <div className="mt-14 fade-in">
        <div className="mb-6 flex items-center gap-3">
          <Lock className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
          <div className="label-eyebrow" style={{ color: "var(--brand-sage-deep)" }}>
            Full report unlocked
          </div>
        </div>

        {/* Chapter 1 */}
        <div className="mb-4">
          <div className="label-eyebrow mb-1">Chapter 1 · Your diagnosis</div>
          <div className="font-serif text-2xl">The four dimensions of your Local Authority</div>
        </div>
        <SubScoresPanel subScores={t.sub_scores} opportunityLevel={t.opportunity_level} />

        {/* Static First Move preview */}
        <div className="mt-16">
          <SampleFirstMove recommendation={t.recommendations[0]} propertyCity={t.property.city} />
        </div>

        {/* Rest of the plan */}
        <div className="mt-8">
          <RestOfPlan recommendations={t.recommendations} />
        </div>
      </div>

      {/* Bottom CTA */}
      <div
        className="mt-16 rounded-2xl p-8 md:p-12 text-center"
        style={{
          background: "linear-gradient(135deg, rgba(107,122,85,0.08), rgba(166,124,63,0.06))",
          border: "1px solid var(--border-default)",
        }}
      >
        <div className="label-eyebrow mb-3">Ready to see yours?</div>
        <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
          Grade your hotel next.
        </h2>
        <p className="text-[color:var(--text-secondary)] max-w-xl mx-auto mb-6 leading-relaxed">
          Takes about 1&ndash;2 minutes. See your real Local Authority Score before entering any contact
          information.
        </p>
        <Link to="/" className="btn-gold !py-3 !px-6" data-testid="sample-report-cta-bottom">
          <Sparkles className="w-4 h-4" />
          See my Local Authority Score
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}


/**
 * A static, non-interactive version of the FirstMove card. Mirrors the real
 * component's structure and copy but the "Draft my first guide" button is a
 * disabled sample chip — clicking anywhere sends the visitor to the grader
 * to make their own.
 */
function SampleFirstMove({ recommendation, propertyCity }) {
  return (
    <div
      className="rounded-3xl p-8 md:p-12 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, rgba(107,122,85,0.10), rgba(166,124,63,0.06))",
        border: "1px solid rgba(107,122,85,0.28)",
      }}
    >
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 mb-4">
            <PenTool className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
            <div className="label-eyebrow">Improve your Local Authority Score</div>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-6">
            We&apos;ve drafted a FieldGuide to help improve your Local Authority Score.
          </h2>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-lg mb-4">
            We&apos;ve called it{" "}
            <span
              className="font-serif italic"
              style={{ color: "var(--text-primary)" }}
            >
              &ldquo;{recommendation.title}&rdquo;
            </span>
            {" "}and we built it to address a key local-area opportunity for
            your hotel.
          </p>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-3">
            But it&apos;s really important to note:{" "}
            <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>
              THIS IS ONLY A DRAFT.
            </span>{" "}
            The next step is for you to fine-tune the FieldGuide &mdash; with
            insights only you and your team can provide.
          </p>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-3">
            We&apos;ve suggested popular activities and restaurants &mdash; you
            should update these with your own descriptions, or replace them
            entirely.
          </p>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-3">
            We&apos;ve structured the FieldGuide with AI criteria in mind. So
            when you add your local perspective, you&apos;ll have a much better
            chance of breaking through.
          </p>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-6">
            And when it comes to web-publishing, your FieldGuide takes care of
            the critical details: from brand-compatible fonts and colors, to
            mobile-first design, to ADA compliance and more. That means your
            content will work just as well for your on-property guests as it
            does for AI.
          </p>

          <div className="mb-6">
            <div className="label-eyebrow mb-2">Your suggested first piece</div>
            <div
              className="rounded-xl p-5"
              style={{ background: "var(--bg-card-warm)", border: "1px solid var(--border-default)" }}
            >
              <div className="font-serif text-xl mb-2">{recommendation.title}</div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="pill pill-neutral">{recommendation.content_type}</span>
                <span className="pill pill-neutral">Effort: {recommendation.effort}</span>
                <span className="pill pill-neutral">Impact: {recommendation.impact}</span>
              </div>
              <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                {recommendation.why_it_matters}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Link to="/" className="btn-gold text-base !py-3 !px-5" data-testid="sample-report-first-move-cta">
              <Sparkles className="w-4 h-4" />
              Grade my hotel to see mine
              <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="text-xs italic" style={{ color: "var(--text-muted)" }}>
              (Sample view — not connected to a live draft.)
            </span>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div
            className="rounded-2xl aspect-[4/5] overflow-hidden"
            style={{
              background:
                "linear-gradient(160deg, rgba(107,122,85,0.32), rgba(166,124,63,0.22) 60%, rgba(166,124,63,0.10))",
            }}
          >
            <div className="w-full h-full flex items-center justify-center p-6 text-center">
              <div>
                <div className="label-eyebrow mb-2" style={{ color: "var(--brand-sage-deep)" }}>
                  Draft preview
                </div>
                <div className="font-serif text-2xl leading-snug" style={{ color: "var(--text-primary)" }}>
                  &ldquo;{recommendation.title}&rdquo;
                </div>
                <div className="mt-3 text-xs italic" style={{ color: "var(--text-muted)" }}>
                  Hero image and draft body would render in your live guide.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
